/**
 * Security — who an Enterprise organization's people are, and how they get
 * in: the domains it proved, single sign-on, provisioning, required factors.
 *
 * One read (`GET /api/orgs/:id/identity`) feeds every section, and every
 * write answers with the same record, so a section never shows something the
 * one beside it has already changed. Each section is wrapped in
 * `EnterpriseOnly`: on Team the page draws what is missing and who can change
 * it, and never asks the Worker a question it would answer 402.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { color } from '../../theme/tokens';
import { Empty } from '../../components/primitives';
import { EnterpriseOnly } from '../../components/EnterpriseBadge';
import { api, apiPath } from '../../lib/api';
import { isEnterpriseOrg } from '../../lib/billing';
import type { Organization, OrgIdentity } from '../../lib/types';
import { failureText } from '../cloud/shared';
import { DomainsSection } from './Domains';
import { SsoSection } from './Sso';
import { ScimSection } from './Scim';
import { TwoFactorPolicySection } from './TwoFactorPolicy';

/** What a section is handed: the record, and the one way to change it. */
export interface SectionProps {
  organization: Organization;
  identity: OrgIdentity;
  /** Run a write that answers with the record, and draw what it answered. */
  apply: (write: () => Promise<OrgIdentity>) => Promise<boolean>;
  reload: () => Promise<void>;
}

const FEATURE: Record<string, string> = {
  domains: 'Verified domains',
  sso: 'Single sign-on',
  scim: 'Provisioning',
  twofactor: 'Required two-factor'
};

export function SecurityPage({
  organization,
  section
}: {
  organization: Organization | null;
  section: string | null;
}): React.JSX.Element {
  const [identity, setIdentity] = useState<OrgIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const orgId = organization?.id ?? null;
  const enterprise = isEnterpriseOrg(organization);

  const reload = useCallback(async (): Promise<void> => {
    if (!orgId || !enterprise) return;
    try {
      setIdentity(await api.get<OrgIdentity>(apiPath`/orgs/${orgId}/identity`));
    } catch (failure) {
      setError(failureText(failure));
    }
  }, [orgId, enterprise]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const apply = useCallback(async (write: () => Promise<OrgIdentity>): Promise<boolean> => {
    setError(null);
    try {
      setIdentity(await write());
      return true;
    } catch (failure) {
      setError(failureText(failure));
      return false;
    }
  }, []);

  if (!organization) return <Empty title="No organization">Security belongs to an organization.</Empty>;
  const key = section ?? 'domains';
  const feature = FEATURE[key] ?? 'This';

  return (
    <EnterpriseOnly organization={organization} feature={feature}>
      {error && <p role="alert" style={{ margin: 0, fontSize: 13.5, color: color.badText }}>{error}</p>}
      {identity === null ? (
        <Empty title="Reading this organization’s security…" />
      ) : (
        <Section name={key} props={{ organization, identity, apply, reload }} />
      )}
    </EnterpriseOnly>
  );
}

function Section({ name, props }: { name: string; props: SectionProps }): React.JSX.Element {
  switch (name) {
    case 'sso':
      return <SsoSection {...props} />;
    case 'scim':
      return <ScimSection {...props} />;
    case 'twofactor':
      return <TwoFactorPolicySection {...props} />;
    default:
      return <DomainsSection {...props} />;
  }
}
